import { useRef, useState, type KeyboardEvent } from "react";
import { BatteryFull, Info, Loader2, RotateCcw, SendHorizontal, Signal, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export interface SandboxWorkflow {
  id: string;
  title: string;
  /** Only workflows that can run can be tested. */
  available: boolean;
}

interface WorkflowSandboxProps {
  businessId?: string | null;
  workflows: readonly SandboxWorkflow[];
}

// The follow-up workflow: the only one with waves to skip ahead to.
const FOLLOW_UP_WORKFLOW_ID = "idle-lead-winback";

const CUSTOMER_TYPES = [
  { value: "new-lead", label: "New lead", hint: "Just sent their first message and hasn't booked." },
  { value: "quiet-lead", label: "Lead who went quiet", hint: "Enquired earlier but stopped replying." },
  { value: "trial", label: "Trial member", hint: "Has booked a trial and hasn't joined yet." },
] as const;

const MAX_MESSAGE_LENGTH = 1000;
const MAX_SCENARIO_LENGTH = 500;

interface SandboxMessage {
  id: number;
  from: "customer" | "business" | "note";
  text: string;
  /** Business messages only: which follow-up wave this was. */
  wave?: number;
}

/** What shaped the latest reply (supabase/functions/_shared/sandbox/workflowSandbox.ts). */
interface SandboxWhy {
  instructions: string[];
  knowledge: string[];
  stop: string;
}

// Replies come from the workflow-definitions "sandbox" action: built from the
// saved settings and Knowledge like a live reply, but nothing is sent or saved.
const WorkflowSandbox = ({ businessId, workflows }: WorkflowSandboxProps) => {
  const firstAvailable = workflows.find((workflow) => workflow.available)?.id ?? "";
  const [workflowId, setWorkflowId] = useState(firstAvailable);
  const [customerType, setCustomerType] = useState<(typeof CUSTOMER_TYPES)[number]["value"]>("new-lead");
  const [scenario, setScenario] = useState("");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<SandboxMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [finished, setFinished] = useState(false);
  const [why, setWhy] = useState<SandboxWhy | null>(null);
  const nextId = useRef(1);
  const [clock] = useState(() => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(/\s?[AP]M$/i, ""));

  const workflow = workflows.find((item) => item.id === workflowId);
  const customer = CUSTOMER_TYPES.find((item) => item.value === customerType);
  const canSend = draft.trim().length > 0 && !pending && !!businessId && !!workflow?.available;
  const isFollowUp = workflowId === FOLLOW_UP_WORKFLOW_ID;

  const append = (message: Omit<SandboxMessage, "id">) =>
    setMessages((current) => [...current, { ...message, id: nextId.current++ }]);

  const ask = async (conversation: SandboxMessage[], mode: "reply" | "next_follow_up") => {
    if (!businessId) return;
    setPending(true);
    try {
      const { data, error } = await supabase.functions.invoke("workflow-definitions", {
        body: {
          action: "sandbox",
          business_id: businessId,
          workflow: workflowId,
          customer: customerType,
          scenario,
          mode,
          messages: conversation
            .filter((message) => message.from !== "note")
            .map(({ from, text, wave }) => ({ from, text, ...(wave ? { wave } : {}) })),
        },
      });
      if (error || !data?.success) {
        const response = (error as { context?: Response } | null)?.context;
        const details = response instanceof Response ? await response.clone().json().catch(() => null) : null;
        throw new Error(details?.error ?? data?.error ?? "Dibs couldn't reply just now. Try again.");
      }
      if (typeof data.reply === "string" && data.reply) {
        append({ from: "business", text: data.reply, ...(data.wave ? { wave: data.wave } : {}) });
      }
      if (data.finished) {
        setFinished(true);
        append({ from: "note", text: data.why?.stop ?? "Dibs stops following up here." });
      }
      setWhy(data.why ?? null);
    } catch (error) {
      append({ from: "note", text: error instanceof Error ? error.message : "Dibs couldn't reply just now. Try again." });
    } finally {
      setPending(false);
    }
  };

  const send = () => {
    const text = draft.trim();
    if (!text || pending) return;
    const conversation = [...messages, { id: nextId.current++, from: "customer" as const, text }];
    setMessages(conversation);
    setDraft("");
    // A reply after the follow-ups ended starts them over, as a real reply would.
    setFinished(false);
    void ask(conversation, "reply");
  };

  const skipAhead = () => {
    if (pending) return;
    void ask(messages, "next_follow_up");
  };

  const onDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([]);
    setDraft("");
    setWhy(null);
    setFinished(false);
  };

  return (
    <div
      data-testid="workflow-sandbox"
      className="grid min-h-0 flex-1 gap-6 overflow-y-auto bg-background p-6 lg:grid-cols-[300px_minmax(0,1fr)_300px]"
    >
      <section aria-label="Sandbox setup" className="module-card space-y-5 self-start">
        <div>
          <h2 className="font-heading text-base font-semibold leading-6 text-foreground">Set up a test</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pretend to be a customer and see how Dibs would reply. Nothing is sent to real customers.
          </p>
        </div>

        <div className="space-y-2">
          <p id="sandbox-workflow-label" className="text-sm font-medium text-foreground">Workflow</p>
          <Select value={workflowId} onValueChange={setWorkflowId}>
            <SelectTrigger aria-labelledby="sandbox-workflow-label" className="w-full">
              <SelectValue placeholder="Choose a workflow" />
            </SelectTrigger>
            <SelectContent>
              {workflows.map((item) => (
                <SelectItem key={item.id} value={item.id} disabled={!item.available}>
                  {item.title}{item.available ? "" : " (coming soon)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <p id="sandbox-customer-label" className="text-sm font-medium text-foreground">Customer</p>
          <Select value={customerType} onValueChange={(value) => setCustomerType(value as typeof customerType)}>
            <SelectTrigger aria-labelledby="sandbox-customer-label" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CUSTOMER_TYPES.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {customer && <p className="text-sm text-muted-foreground">{customer.hint}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sandbox-scenario">Scenario note (optional)</Label>
          <Textarea
            id="sandbox-scenario"
            value={scenario}
            maxLength={MAX_SCENARIO_LENGTH}
            placeholder="e.g. Asked about weekend classes and prices last Tuesday."
            className="min-h-[96px]"
            onChange={(event) => setScenario(event.target.value)}
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={messages.length === 0 && !draft}
          className="standard-action-button neutral-action-button w-full gap-1.5"
          onClick={reset}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset conversation
        </Button>
      </section>

      <section aria-label="Test conversation" className="flex min-w-0 flex-col items-center gap-4">
        <div className="flex w-full max-w-[420px] items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate font-heading text-base font-semibold leading-6 text-foreground">
              {workflow?.title ?? "No workflow chosen"}
            </h2>
            <p className="text-sm text-muted-foreground">Testing as: {customer?.label.toLocaleLowerCase()}</p>
          </div>
          {isFollowUp && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending || finished || !businessId}
              title={finished ? "The follow-ups have ended. Reset or reply as the customer to test again." : "Pretend the lead stayed quiet until the next follow-up"}
              className="compact-action-button shrink-0"
              onClick={skipAhead}
            >
              Skip to next follow-up
            </Button>
          )}
        </div>

        <div className="flex w-full max-w-[420px] items-start gap-2.5 text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-xs leading-5">
            Practice replies use your saved settings and Knowledge. Nothing is sent to customers, and the wording can differ a little from a live reply.
          </p>
        </div>

        {/* Phone mock-up: the customer's side of the conversation. */}
        <div
          className="relative flex h-[620px] w-[310px] shrink-0 flex-col overflow-hidden rounded-[44px] border-[10px] border-foreground bg-card shadow-xl"
          aria-label="Phone preview"
        >
          <div className="pointer-events-none absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-foreground" aria-hidden="true" />

          <div className="flex items-center justify-between px-6 pb-1 pt-3 text-[11px] font-semibold text-foreground" aria-hidden="true">
            <span>{clock}</span>
            <span className="flex items-center gap-1">
              <Signal className="h-3 w-3" />
              <BatteryFull className="h-3.5 w-3.5" />
            </span>
          </div>

          <header className="flex items-center gap-2.5 border-b border-border px-4 pb-3 pt-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full gradient-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">Your business</p>
              <p className="text-[11px] text-muted-foreground">Replies instantly with Dibs</p>
            </div>
          </header>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-background px-3 py-4" aria-live="polite">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                <p className="text-sm font-medium text-foreground">Start the conversation</p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  Type a message as the customer, such as &ldquo;Hi, how much is a trial class?&rdquo;
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) =>
                  message.from === "note" ? (
                    <p key={message.id} role="status" className="px-2 pt-1 text-center text-[11px] leading-4 text-muted-foreground">
                      {message.text}
                    </p>
                  ) : message.from === "business" ? (
                    <div key={message.id} className="flex flex-col items-start">
                      {message.wave && (
                        <span className="mb-1 pl-1 text-[11px] text-muted-foreground">Follow-up {message.wave}</span>
                      )}
                      <p className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-muted px-3.5 py-2 text-sm leading-relaxed text-foreground">
                        {message.text}
                      </p>
                    </div>
                  ) : (
                    <div key={message.id} className="flex justify-end">
                      <p className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm leading-relaxed text-primary-foreground">
                        {message.text}
                      </p>
                    </div>
                  ),
                )}
                {pending && (
                  <p className="flex items-center gap-1.5 pl-1 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    Dibs is replying…
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-border bg-card px-3 pb-5 pt-2.5">
            <Textarea
              value={draft}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={1}
              aria-label="Message as the customer"
              placeholder="Message"
              className="max-h-24 min-h-[36px] flex-1 resize-none rounded-full px-4 py-2 text-sm"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onDraftKeyDown}
            />
            <button
              type="button"
              disabled={!canSend}
              aria-label="Send message"
              onClick={send}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
            >
              <SendHorizontal className="h-4 w-4" />
            </button>
          </div>

          <div className="pointer-events-none absolute bottom-1.5 left-1/2 h-1 w-24 -translate-x-1/2 rounded-full bg-foreground/80" aria-hidden="true" />
        </div>
      </section>

      <section aria-label="Why Dibs replied this way" className="module-card space-y-4 self-start">
        <div>
          <h2 className="font-heading text-base font-semibold leading-6 text-foreground">Why Dibs replied this way</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            After each reply, you&apos;ll see what shaped it here.
          </p>
        </div>
        <ul className="space-y-3">
          {([
            ["Instructions", "The workflow instructions Dibs followed.", why?.instructions ?? []],
            ["Knowledge and offers", "The notes, FAQs and offers it drew on.", why?.knowledge ?? []],
            ["Stop or handover", "Whether it would have stopped or passed the chat to your team.", why ? [why.stop] : []],
          ] as const).map(([title, hint, lines]) => (
            <li
              key={title}
              className={`rounded-lg border px-4 py-3 ${why ? "border-border bg-card" : "border-dashed border-border bg-muted/30"}`}
            >
              <p className="text-sm font-medium text-foreground">{title}</p>
              {!why ? (
                <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>
              ) : lines.length === 0 ? (
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {title === "Instructions" ? "Only Dibs' standard behaviour." : "None for this reply."}
                </p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {lines.map((line) => (
                    <li key={line} className="break-words text-sm text-muted-foreground">{line}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">
          The sandbox uses your saved settings. Changes save as you make them.
        </p>
      </section>
    </div>
  );
};

export default WorkflowSandbox;
